// Shared parameter-value validator (Phase 3/4 + hardening). Pure; imports only types so the SAME logic
// runs in the browser (resolveReference) AND the Deno edge function (server-authoritative resolution) via
// the synced copy. Rejects unknown params, wrong types, and out-of-bounds numerics — never silently
// clamps or drops, so a tampered value fails loudly.

import type { ParameterSchema } from '../../types/customBlockLibrary';

export type ParamValidation =
  | { ok: true; values: Record<string, unknown> }
  | { ok: false; errors: string[] };

export function validateParameters(schema: ParameterSchema, params: Record<string, unknown> = {}): ParamValidation {
  const errors: string[] = [];
  const values: Record<string, unknown> = {};
  for (const key of Object.keys(params)) {
    if (key === '_ranges' || key === '__canonical') continue; // engine bookkeeping, not user params
    // Own-property check ONLY: `key in schema` walks the prototype chain, so object-machinery names
    // ('__proto__', 'constructor', 'toString', …) would silently pass the unknown-parameter rejection.
    if (!Object.prototype.hasOwnProperty.call(schema, key)) errors.push(`unknown parameter '${key}'`);
  }
  for (const [name, spec] of Object.entries(schema)) {
    const raw = name in params ? params[name] : spec.default;
    if (spec.kind === 'int' || spec.kind === 'float') {
      const n = Number(raw);
      if (typeof raw !== 'number' || !Number.isFinite(n)) { errors.push(`'${name}' must be a finite number`); continue; }
      if (spec.kind === 'int' && !Number.isInteger(n)) { errors.push(`'${name}' must be an integer`); continue; }
      if (typeof spec.min === 'number' && n < spec.min) { errors.push(`'${name}'=${n} below min ${spec.min}`); continue; }
      if (typeof spec.max === 'number' && n > spec.max) { errors.push(`'${name}'=${n} above max ${spec.max}`); continue; }
      values[name] = n;
    } else if (spec.kind === 'bool') {
      if (typeof raw !== 'boolean') { errors.push(`'${name}' must be boolean`); continue; }
      values[name] = raw;
    } else if (spec.kind === 'enum') {
      if (!Array.isArray(spec.values) || !spec.values.includes(raw as string | number)) { errors.push(`'${name}' must be one of ${(spec.values ?? []).join('|')}`); continue; }
      values[name] = raw;
    }
  }
  return errors.length ? { ok: false, errors } : { ok: true, values };
}
